import PropTypes from 'prop-types';
import get from 'lodash/get';
import { ReactSVG } from 'react-svg';
import playerStatuses from 'src/common/utils/playerStatuses';
import getPlayedProgressSvgPath from 'src/common/utils/playerStatus/getPlayedProgressSvgPath';
import {
  playIconPlayerStatuses,
  playerStatusToSvgIconMap,
} from 'src/common/utils/playerStatusToIconMap';
import CircularProgress from '../../../../shared/CircularProgress';
import PopOutButton from '../../../../shared/button/PopOutButton';
import css from './play-button.module.scss';

/**
 * General styles for button
 */
const styles = {
  button: {
    height: '35px',
    width: '35px',
  },
};

/**
 * PlayButton for profile list item
 *
 * @param {string} syncedPlayerStatus - The current status of the player
 * @param {boolean} canScrub - Indicates whether the stream can be scrubbed
 * @param {string} color - The color of the button
 * @param {string} playbackStatus - The status of playback
 * @param {string} playerStatus - The type of player to be used
 * @param {string} guideItem - The guide item that is rendering the play button
 * @param {string} tunedGuideId - The currently tuned guideId in the player
 * @param {string} externalHtmlPlayerUrl - The URL of the stream to play if playerStatus is 'popout'
 */
const PlayButton = ({
  syncedPlayerStatus,
  canScrub,
  color,
  playbackStatus,
  playerStatus,
  externalHtmlPlayerUrl,
  guideItem,
  tunedGuideId,
}) => {
  const guideId = get(guideItem, 'guideId');
  /**
   * Check current status of player and display progress button
   * if player is connecting and/or preroll
   */
  if (
    syncedPlayerStatus === playerStatuses.connecting ||
    syncedPlayerStatus === playerStatuses.preroll
  ) {
    return (
      <div className={css.playProgressContainer}>
        <CircularProgress
          data-testid="statusPlayerLoadingSpinner"
          color={color}
          size={35}
          thickness={1.5}
        />
      </div>
    );
  }

  /**
   * If there is a tunedGuideId in the player and the currentGuide item has a guideId
   * and they are the same and status is a popout, render and provide the stream URL
   */
  if (
    tunedGuideId &&
    guideId &&
    tunedGuideId === guideId &&
    playerStatus === playerStatuses.popout
  ) {
    return (
      <div className={css.popOutButton}>
        <PopOutButton
          externalHtmlPlayerUrl={externalHtmlPlayerUrl}
          dataTestId={`player-status-${playerStatus}`}
          width={styles.button.width}
          height={styles.button.height}
        />
      </div>
    );
  }

  /*
   playbackStatus object: ---------
   duration: "00:51:00"
   progressInSeconds: 0 // not present if has not been tuned and there has been no report call
   progressPercent: 0 // not present if has not been tuned and there has been no report call
   status: "PlayedThreshold1"
   playbackStatus.statusIcon: "playedProgress1" // not present if has not been tuned
  */

  if (playIconPlayerStatuses[syncedPlayerStatus]) {
    return (
      <div className={css.playProgressContainer} data-icon="play">
        <ReactSVG
          src={getPlayedProgressSvgPath(playbackStatus.statusIcon)}
          beforeInjection={(svg) => {
            // note: IE11 does not support Element.classlist for SVG elements
            svg.setAttribute('class', css.playButton);
          }}
        />
      </div>
    );
  }

  /**
   * Otherwise display a representational button based on current status
   */
  const PlayButtonStateRepresentation =
    playerStatusToSvgIconMap[syncedPlayerStatus](canScrub);
  return (
    <div className={css.playProgressContainer}>
      <PlayButtonStateRepresentation
        id="listPlayerActionButton"
        dataTestId="listPlayerActionButton"
        className={css.playButton}
        width={styles.button.width}
        height={styles.button.height}
      />
    </div>
  );
};

PlayButton.propTypes = {
  syncedPlayerStatus: PropTypes.string.isRequired,
  playbackStatus: PropTypes.object.isRequired,
  canScrub: PropTypes.bool,
  color: PropTypes.string.isRequired,
  guideItem: PropTypes.object,
  tunedGuideId: PropTypes.string,
  playerStatus: PropTypes.string,
  externalHtmlPlayerUrl: PropTypes.string,
};

export default PlayButton;
